import { useContext, useState, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Text,
  useToast,
  FormErrorMessage,
  FormHelperText,
  Heading,
  Select,
  InputGroup,
  InputRightElement,
  IconButton,
  SimpleGrid,
  Flex,
  Spinner,
  useColorModeValue,
  Switch,
  VStack,
  Badge,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';

import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  apiCreateNewUser,
  apiGetBranches,
  apiUpdateUser,
  apiGetUsers,
  apiGetProducerCodes,
  apiAdminGetBadges,
  apiAdminAssignBadge,
  apiAdminRemoveBadge,
} from 'api';

import { useUserActurisSyncStatus } from './useUserActurisSyncStatus';

import type {
  APIBranchData,
  APIUserListResultData,
  APIProducerCodeData,
} from 'types/api.types';

import { ROLE_ADMIN } from 'variables/constants';

import { ROLES_OPTIONS, PROVINCES } from 'variables/options';

import Card from 'components/card/Card';
import MultiSelect from 'components/fields/MultiSelect';
import SelectSearch from 'components/fields/SelectSearch';

import { AuthContext } from 'contexts/AuthContext';

const STATUS_OPTIONS = [
  { value: true, label: 'Active' },
  { value: false, label: 'Inactive' },
];

const UserFormView = () => {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    password: '',
    role: '' as unknown as number,
    branches: [] as number[],
    isActive: true,
    isGeofenced: false,
    subordinates: [] as number[],
    producerCodes: [] as number[],
    address: '',
    city: '',
    province: '',
    postalCode: '',
    csrCode: '',
    acturisUsername: '',
    acturisAccountExecutive: '',
    acturisAccountHandler: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [showPassword, setShowPassword] = useState(false);

  const {
    userData,
    syncData,
    isLoading: isLoadingUser,
    error: userError,
    resync,
    isResyncing,
  } = useUserActurisSyncStatus(isEditMode ? parseInt(id!) : undefined);

  const {
    data: branches = [],
    isLoading: loadingBranches,
    isFetching: isFetchingBranches,
  } = useQuery<APIBranchData[]>({
    queryKey: ['branches-all'],
    queryFn: () => apiGetBranches(null, 'all'),
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: producerCodes = [],
    isLoading: loadingProducerCodes,
    isFetching: isFetchingProducerCodes,
  } = useQuery<APIProducerCodeData[]>({
    queryKey: ['producer-codes-all'],
    queryFn: () => apiGetProducerCodes(null, 'all'),
  });

  const selectedBranchIds = formData.branches;
  const selectedBranchIdsParam = selectedBranchIds.join(',');

  const {
    data: availableUsers = [],
    isLoading: loadingUsers,
    isFetching: isFetchingUsers,
  } = useQuery({
    queryKey: ['users', `branch-${selectedBranchIdsParam}`],
    queryFn: () => apiGetUsers(null, `all&branches=${selectedBranchIdsParam}`),
    enabled: selectedBranchIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  }) as {
    data: APIUserListResultData[];
    isLoading: boolean;
    isFetching: boolean;
  };

  useEffect(() => {
    if (userData && isEditMode) {
      setFormData({
        email: userData.user.email || '',
        firstName: userData.user.first_name || '',
        lastName: userData.user.last_name || '',
        phone: userData.phone || '',
        password: '',
        role: userData.role,
        branches: userData.branches,
        isActive: userData.is_active ?? true,
        isGeofenced: userData.is_geofenced ?? false,
        subordinates: userData.subordinates || [],
        producerCodes: userData.producer_codes || [],
        address: userData.address || '',
        city: userData.city || '',
        province: userData.province_display || '',
        postalCode: userData.postal_code || '',
        csrCode: userData.csr_code || '',
        acturisUsername: userData.acturis_username || '',
        acturisAccountExecutive: userData.acturis_account_executive || '',
        acturisAccountHandler: userData.acturis_account_handler || '',
      });
    }
  }, [userData, isEditMode]);

  const createUserMutation = useMutation({
    mutationFn: (data: any) => apiCreateNewUser(data),
    onSuccess: () => {
      toast({
        title: 'User created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      navigate('/admin/settings/users');
    },
    onError: handleApiError,
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: any }) =>
      apiUpdateUser(userId, data),
    onSuccess: () => {
      toast({
        title: 'User updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['acturis-config-status'] });

      navigate('/admin/settings/users');
    },
    onError: handleApiError,
  });

  const handleResync = () => {
    resync();
    toast({
      title: 'Acturis sync started',
      status: 'info',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
  };

  // Handle API errors
  function handleApiError(error: any) {
    toast({
      title: `Error ${isEditMode ? 'updating' : 'creating'} user`,
      description: 'Please check the form for errors',
      status: 'error',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });

    const formattedErrors: Record<string, string> = {};

    Object.entries(error).forEach(([field, messages]) => {
      if (Array.isArray(messages) && messages.length > 0) {
        formattedErrors[field] = messages[0];
      } else if (typeof messages === 'string') {
        formattedErrors[field] = messages;
      }
    });

    setErrors(formattedErrors);
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    // Handle role changes with smart branch management
    if (name === 'role') {
      setFormData((prev) => {
        const newRole = parseInt(value);
        // Reset to empty when becoming admin
        const newBranches = newRole === ROLE_ADMIN ? [] : prev.branches;

        return {
          ...prev,
          [name]: newRole,
          branches: newBranches,
          subordinates: [], // Still reset subordinates since they're branch-dependent
        };
      });
    }
    // Handle the isActive select which needs to be converted to boolean
    else if (name === 'isActive') {
      setFormData((prev) => ({
        ...prev,
        isActive: value === 'true', // Convert string 'true'/'false' to boolean
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear any previous errors for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Handle branches selection
  const handleBranchesChange = (selected: number[]) => {
    setFormData((prev) => ({
      ...prev,
      branches: selected,
      subordinates: [],
    }));
  };

  // Handle subordinates selection
  const handleSubordinatesChange = (selected: number[]) => {
    setFormData((prev) => ({
      ...prev,
      subordinates: selected,
    }));
  };

  // Handle producer codes selection
  const handleProducerCodesChange = (selected: number[]) => {
    setFormData((prev) => ({
      ...prev,
      producerCodes: selected,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    const usernameChanged =
      formData.acturisUsername !== (userData?.acturis_username || '');

    if (!usernameChanged) {
      if (
        syncData.personnelOptions?.executives &&
        !formData.acturisAccountExecutive
      ) {
        newErrors.acturis_account_executive = 'Account Executive is required';
      }
      if (
        syncData.personnelOptions?.handlers &&
        !formData.acturisAccountHandler
      ) {
        newErrors.acturis_account_handler = 'Account Handler is required';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const userPayload = {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        role: formData.role,
        branches: formData.role === ROLE_ADMIN ? [] : formData.branches,
        is_active: formData.isActive,
        is_geofenced: formData.isGeofenced,
        subordinates: formData.subordinates,
        producer_codes: formData.producerCodes,
        address: formData.address,
        city: formData.city,
        province: formData.province,
        postal_code: formData.postalCode,
        csr_code: formData.csrCode,
        acturis_username: formData.acturisUsername,
        acturis_account_executive:
          formData.acturisAccountExecutive || undefined,
        acturis_account_handler: formData.acturisAccountHandler || undefined,
      };

      // Only include password if it's not empty (for edit mode)
      if (formData.password) {
        // @ts-ignore
        userPayload.password = formData.password;
      }

      if (isEditMode && id) {
        updateUserMutation.mutate({
          userId: parseInt(id),
          data: userPayload,
        });
      } else {
        createUserMutation.mutate(userPayload);
      }

      queryClient.invalidateQueries({ queryKey: ['lead-users'] });
    } catch (error) {
      toast({
        title: `Error ${isEditMode ? 'updating' : 'creating'} user`,
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const getMaxSelectedBranches = () => {
    const roleNumber = Number(formData.role);

    if (roleNumber === ROLE_ADMIN) {
      return 0; // Admin can't select any (handled by isDisabled)
    }

    return undefined;
  };

  // Show loading state if branches data is loading or user data is loading (in edit mode)
  if (
    loadingBranches ||
    isFetchingBranches ||
    loadingProducerCodes ||
    isFetchingProducerCodes ||
    (isEditMode && isLoadingUser)
  ) {
    return (
      <Card>
        <Flex align="center" justify="center">
          <Spinner size="sm" mr="10px" /> Loading data...
        </Flex>
      </Card>
    );
  }

  // Show error state if user data failed to load (in edit mode)
  if (isEditMode && userError) {
    return (
      <Card>
        <Flex align="center" justify="center" color="red.500">
          Error loading user data. Please try again.
        </Flex>
      </Card>
    );
  }

  const isSubmitting =
    createUserMutation.isPending || updateUserMutation.isPending;

  // Check if subordinates field should be enabled - only require branch selection
  const isSubordinatesEnabled = formData.branches.length > 0;

  return (
    <form onSubmit={handleSubmit}>
      <Card mb="20px">
        <Heading size="md" mb={6} color={textColor}>
          {isEditMode ? 'Edit User' : 'Create New User'}
        </Heading>

        {errors.non_field_errors && (
          <Box mb={6} p={3} borderRadius="md" bg="red.100" color="red.700">
            {errors.non_field_errors}
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <FormControl isRequired isInvalid={!!errors.email}>
            <FormLabel>Email</FormLabel>
            <Input
              variant="main"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email"
              type="email"
            />
            <FormErrorMessage>{errors.email}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired={!isEditMode} isInvalid={!!errors.password}>
            <FormLabel>
              {isEditMode
                ? 'New Password (leave blank to keep current)'
                : 'Password'}
            </FormLabel>
            <InputGroup>
              <Input
                variant="main"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                placeholder={
                  isEditMode ? 'New password (optional)' : 'Password'
                }
              />
              <InputRightElement>
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  variant="ghost"
                  onClick={() => setShowPassword(!showPassword)}
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>
            <FormHelperText>
              Must be at least 8 characters, not a commonly used password, and
              not entirely numeric.
            </FormHelperText>
            <FormErrorMessage>{errors.password}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.firstName}>
            <FormLabel>First Name</FormLabel>
            <Input
              variant="main"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="First Name"
            />
            <FormErrorMessage>{errors.firstName}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.lastName}>
            <FormLabel>Last Name</FormLabel>
            <Input
              variant="main"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Last Name"
            />
            <FormErrorMessage>{errors.lastName}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.phone}>
            <FormLabel>Phone</FormLabel>
            <Input
              variant="main"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone"
            />
            <FormErrorMessage>{errors.phone}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.role}>
            <FormLabel>Role</FormLabel>
            <Select
              variant="main"
              name="role"
              value={formData.role}
              onChange={handleChange}
              placeholder="Select role"
              disabled={user.role !== ROLE_ADMIN}
            >
              {ROLES_OPTIONS.map((roleOption) => (
                <option key={roleOption.value} value={roleOption.value}>
                  {roleOption.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.role}</FormErrorMessage>
          </FormControl>

          {/* Status dropdown */}
          <FormControl isRequired isInvalid={!!errors.isActive}>
            <FormLabel>Status</FormLabel>
            <Select
              variant="main"
              name="isActive"
              value={formData.isActive.toString()}
              onChange={handleChange}
            >
              {STATUS_OPTIONS.map((statusOption) => (
                <option
                  key={statusOption.label}
                  value={statusOption.value.toString()}
                >
                  {statusOption.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.isActive}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <Flex alignItems="center">
              <FormLabel htmlFor="isGeofenced" mb="0">
                Geofenced
              </FormLabel>
              <Switch
                id="isGeofenced"
                isChecked={formData.isGeofenced}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isGeofenced: e.target.checked,
                  }))
                }
              />
            </Flex>
            <Text mt={2} pl={2} color="gray.500">
              Restrict file viewing and downloading to Canadian IP addresses
              only
            </Text>
          </FormControl>

          <FormControl isInvalid={!!errors.branches}>
            <FormLabel>Branches</FormLabel>
            <MultiSelect
              selectedIds={formData.branches}
              availableItems={branches}
              isLoading={loadingBranches}
              onChange={handleBranchesChange}
              label="Select branch(es)"
              itemLabelSingular="branch"
              itemLabelPlural="branches"
              isDisabled={
                Number(formData.role) === ROLE_ADMIN || user.role !== ROLE_ADMIN
              }
              maxSelectedItems={getMaxSelectedBranches()}
            />
            {Number(formData.role) === ROLE_ADMIN && (
              <Text mt={2} pl={2} color="gray.500">
                All branches are enabled by default for Admin users
              </Text>
            )}
            <FormErrorMessage>{errors.branches}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.subordinates}>
            <FormLabel>Assigned users</FormLabel>
            <MultiSelect
              selectedIds={formData.subordinates}
              availableItems={availableUsers.map((user) => ({
                id: user.user.id,
                name: `${user.user.first_name} ${user.user.last_name}`,
              }))}
              isLoading={loadingUsers || isFetchingUsers}
              onChange={handleSubordinatesChange}
              label="Select user(s)"
              itemLabelSingular="user"
              itemLabelPlural="users"
              isDisabled={!isSubordinatesEnabled}
            />
            {!isSubordinatesEnabled && (
              <Text mt={2} pl={2} color="gray.500">
                Please select a branch to assign users
              </Text>
            )}
            {isSubordinatesEnabled && (
              <Text mt={2} pl={2} color="gray.500">
                E.g., Broker Partner team members. Broker Account Managers &
                Account Assistant, etc.
              </Text>
            )}
            <FormErrorMessage>{errors.subordinates}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.producerCodes}>
            <FormLabel>Producer Codes</FormLabel>
            <MultiSelect
              selectedIds={formData.producerCodes}
              availableItems={producerCodes}
              isLoading={loadingProducerCodes || isFetchingProducerCodes}
              onChange={handleProducerCodesChange}
              label="Select producer code(s)"
              itemLabelSingular="producer code"
              itemLabelPlural="producer codes"
              isDisabled={false}
            />
            <FormErrorMessage>{errors.producerCodes}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.address} isRequired>
            <FormLabel>Address</FormLabel>
            <Input
              variant="main"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Address"
            />
            <FormErrorMessage>{errors.address}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.city} isRequired>
            <FormLabel>City</FormLabel>
            <Input
              variant="main"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="City"
            />
            <FormErrorMessage>{errors.city}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.province} isRequired>
            <FormLabel>Province</FormLabel>
            <Select
              variant="main"
              name="province"
              value={formData.province}
              onChange={handleChange}
              placeholder="Select province"
            >
              {PROVINCES.map((province) => (
                <option key={province.value} value={province.value}>
                  {province.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.province}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.postalCode} isRequired>
            <FormLabel>Postal Code</FormLabel>
            <Input
              variant="main"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              placeholder="Postal Code"
              maxLength={20}
            />
            <FormErrorMessage>{errors.postalCode}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.csrCode}>
            <FormLabel>CSR Code</FormLabel>
            <Input
              variant="main"
              name="csrCode"
              value={formData.csrCode}
              onChange={handleChange}
              placeholder="CSR Code"
            />
            <FormErrorMessage>{errors.csrCode}</FormErrorMessage>
          </FormControl>
        </SimpleGrid>

        {/* ── Acturis Configuration — admin only, edit mode only ── */}
        {user.role === ROLE_ADMIN && isEditMode && (
          <Box mt={8}>
            <Divider mb={6} />
            <Heading size="sm" mb={4}>
              Acturis Configuration
            </Heading>

            {syncData.syncStatus === 'error' && syncData.syncError && (
              <Alert status="error" mb={4} borderRadius="md">
                <AlertIcon />
                Sync failed: {syncData.syncError}
              </Alert>
            )}

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <FormControl isInvalid={!!errors.acturis_username}>
                <FormLabel>Acturis Username</FormLabel>
                <Input
                  variant="main"
                  name="acturisUsername"
                  value={formData.acturisUsername}
                  onChange={handleChange}
                  placeholder="Broker's Acturis username"
                />
                <FormErrorMessage>{errors.acturis_username}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>
                  Sync Status
                  {syncData.syncStatus === 'syncing' && (
                    <Spinner size="xs" ml={2} />
                  )}
                </FormLabel>
                <Box>
                  {syncData.syncStatus === 'not_configured' && (
                    <Badge colorScheme="gray">Not configured</Badge>
                  )}
                  {!syncData.syncStatus && syncData.hasUsername && (
                    <Badge colorScheme="gray">Not synced</Badge>
                  )}
                  {syncData.syncStatus === 'syncing' && (
                    <Badge colorScheme="blue">Syncing...</Badge>
                  )}
                  {syncData.syncStatus === 'ready' && (
                    <Badge colorScheme="green">Synced</Badge>
                  )}
                  {syncData.syncStatus === 'incomplete' && (
                    <Badge colorScheme="yellow">Incomplete</Badge>
                  )}
                  {syncData.syncStatus === 'error' && (
                    <Badge colorScheme="red">Error</Badge>
                  )}
                  <Button
                    size="xs"
                    variant="outline"
                    ml={3}
                    onClick={handleResync}
                    isLoading={isResyncing}
                    isDisabled={
                      !syncData.hasUsername || syncData.syncStatus === 'syncing'
                    }
                  >
                    Re-sync
                  </Button>
                </Box>
              </FormControl>

              <FormControl>
                <FormLabel>Office</FormLabel>
                <Input
                  variant="main"
                  value={
                    syncData.display.office || syncData.config.office || '-'
                  }
                  isReadOnly
                  bg="gray.100"
                />
              </FormControl>

              <FormControl
                isRequired={!!syncData.personnelOptions?.executives}
                isInvalid={!!errors.acturis_account_executive}
              >
                <FormLabel>Executive</FormLabel>
                {syncData.personnelOptions?.executives ? (
                  <SelectSearch
                    options={syncData.personnelOptions.executives.map(
                      (exec) => ({
                        value: exec.id,
                        label: exec.label,
                      }),
                    )}
                    value={formData.acturisAccountExecutive}
                    onChange={(value) => {
                      setFormData((prev) => ({
                        ...prev,
                        acturisAccountExecutive: value?.toString() || '',
                      }));
                      if (errors.acturis_account_executive) {
                        setErrors((prev) => ({
                          ...prev,
                          acturis_account_executive: '',
                        }));
                      }
                    }}
                    placeholder="Search executive..."
                    isInvalid={!!errors.acturis_account_executive}
                  />
                ) : (
                  <Input
                    variant="main"
                    value={
                      syncData.display.executive ||
                      syncData.config.accountExecutive ||
                      '-'
                    }
                    isReadOnly
                    bg="gray.100"
                  />
                )}
                <FormErrorMessage>
                  {errors.acturis_account_executive}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                isRequired={!!syncData.personnelOptions?.handlers}
                isInvalid={!!errors.acturis_account_handler}
              >
                <FormLabel>Handler</FormLabel>
                {syncData.personnelOptions?.handlers ? (
                  <SelectSearch
                    options={syncData.personnelOptions.handlers.map(
                      (handler) => ({
                        value: handler.id,
                        label: handler.label,
                      }),
                    )}
                    value={formData.acturisAccountHandler}
                    onChange={(value) => {
                      setFormData((prev) => ({
                        ...prev,
                        acturisAccountHandler: value?.toString() || '',
                      }));
                      if (errors.acturis_account_handler) {
                        setErrors((prev) => ({
                          ...prev,
                          acturis_account_handler: '',
                        }));
                      }
                    }}
                    placeholder="Search handler..."
                    isInvalid={!!errors.acturis_account_handler}
                  />
                ) : (
                  <Input
                    variant="main"
                    value={
                      syncData.display.handler ||
                      syncData.config.accountHandler ||
                      '-'
                    }
                    isReadOnly
                    bg="gray.100"
                  />
                )}
                <FormErrorMessage>
                  {errors.acturis_account_handler}
                </FormErrorMessage>
              </FormControl>
            </SimpleGrid>
          </Box>
        )}

        {/* ── Recognition Badges — admin only, edit mode only ── */}
        {user.role === ROLE_ADMIN && isEditMode && (
          <BadgeAssignSection
            userId={Number(id)}
            currentBadges={(userData as any)?.badges ?? []}
          />
        )}

        <Flex mt={10} justify="flex-end">
          <Button
            variant="outline"
            mr={3}
            onClick={() => navigate('/admin/settings/users')}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            type="submit"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Updating' : 'Creating'}
          >
            {isEditMode ? 'Update User' : 'Create User'}
          </Button>
        </Flex>
      </Card>
    </form>
  );
};

/** Standalone badge assignment widget rendered inside the edit form. */
const BadgeAssignSection: React.FC<{
  userId: number;
  currentBadges: { id: number }[];
}> = ({ userId, currentBadges }) => {
  const toast = useToast();
  const qc = useQueryClient();
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const { data: allBadges = [] } = useQuery<any[]>({
    queryKey: ['admin-badges'],
    queryFn: () =>
      apiAdminGetBadges().then((r: any) =>
        Array.isArray(r) ? r : (r?.data ?? []),
      ),
  });

  const activeBadges = allBadges.filter((b) => b.is_active);
  const assignedIds = new Set(currentBadges.map((b) => b.id));

  const assignMutation = useMutation({
    mutationFn: (badgeId: number) => apiAdminAssignBadge(userId, badgeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', String(userId)] });
      toast({ title: 'Badge assigned', status: 'success', duration: 1500 });
    },
    onError: () => toast({ title: 'Failed to assign badge', status: 'error' }),
  });

  const removeMutation = useMutation({
    mutationFn: (badgeId: number) => apiAdminRemoveBadge(userId, badgeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', String(userId)] });
      toast({ title: 'Badge removed', status: 'success', duration: 1500 });
    },
    onError: () => toast({ title: 'Failed to remove badge', status: 'error' }),
  });

  if (activeBadges.length === 0) return null;

  return (
    <Box mt={8}>
      <Divider mb={6} />
      <Heading size="sm" mb={4}>
        Recognition Badges
      </Heading>
      <VStack align="start" spacing={3}>
        {activeBadges.map((badge) => (
          <Checkbox
            key={badge.id}
            isChecked={assignedIds.has(badge.id)}
            onChange={(e) =>
              e.target.checked
                ? assignMutation.mutate(badge.id)
                : removeMutation.mutate(badge.id)
            }
          >
            <HStack spacing={2}>
              {badge.image_url && (
                <img
                  src={badge.image_url}
                  alt={badge.name}
                  style={{
                    height: '20px',
                    width: '20px',
                    objectFit: 'contain',
                  }}
                />
              )}
              <Text fontSize="sm">{badge.name}</Text>
              {badge.description && (
                <Text fontSize="xs" color={mutedColor}>
                  — {badge.description}
                </Text>
              )}
            </HStack>
          </Checkbox>
        ))}
      </VStack>
    </Box>
  );
};

export default UserFormView;
